/* eslint-disable react/no-unescaped-entities */
/**
 * Negociation.jsx — Landing publique CONFILEX Négociation.
 *
 * Charte stricte (`/app/memory/NEGOCIATION_CHARTER.md`) :
 *  - Vocabulaire : "proposition de règlement", "conditions déclarées",
 *    "synthèse des échanges". JAMAIS "contrat", "accord signé", "transaction".
 *  - Présentation : outil de structuration documentaire + facilitation des
 *    échanges amiables. PAS un cabinet juridique, PAS de la médiation
 *    réglementée, PAS du recouvrement.
 *  - Clause de conformité affichée systématiquement (footer + sticky bottom).
 */
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { ldBreadcrumb, ldFAQ, ldService } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import {
  Handshake, FileText, ShieldCheck, Clock, Inbox, ArrowRight,
  ListOrdered, FilePieChart, Lock, Sparkles,
} from "lucide-react";

const COMPLIANCE = (
  "Confilex est un outil de structuration documentaire et de formalisation " +
  "d'informations fournies par les utilisateurs. Confilex ne fournit aucune " +
  "consultation juridique, n'analyse pas les droits des parties, ne vérifie " +
  "pas la validité des créances et ne rédige aucun acte juridique."
);

const PILLARS = [
  { icon: ListOrdered,  title: "Historique horodaté",  desc: "Chaque action est tracée à la seconde près et conservée 18 mois." },
  { icon: Inbox,        title: "Dépôt documentaire",   desc: "Centralisez factures, courriers, justificatifs et pièces (15 Mo/fichier)." },
  { icon: Handshake,    title: "Suivi des échanges",   desc: "Propositions, contre-propositions et messages dans un même fil." },
  { icon: FilePieChart, title: "Export PDF horodaté",  desc: "Synthèse des conditions déclarées par les parties, prête à archiver." },
  { icon: Lock,         title: "Espace sécurisé",      desc: "Accès par lien à token unique, données chiffrées au repos." },
  { icon: ShieldCheck,  title: "Traçabilité complète", desc: "Audit trail technique disponible en option premium." },
];

const STEPS = [
  { n: 1, label: "Création du dossier",
    body: "Renseignez l'identité des parties, l'objet du dossier, le montant concerné et l'échéancier souhaité." },
  { n: 2, label: "Génération d'une proposition de règlement",
    body: "CONFILEX formalise les éléments communiqués — sans interprétation juridique." },
  { n: 3, label: "Invitation de l'autre partie",
    body: "Un email positif et constructif lui est adressé avec un lien sécurisé d'accès au dossier." },
  { n: 4, label: "Échanges horodatés",
    body: "Messages, contre-propositions, dépôt de pièces — tout est tracé et consultable." },
  { n: 5, label: "Synthèse exportée",
    body: "Téléchargez la « Synthèse des conditions déclarées par les parties » au format PDF." },
];

const ROLES = [
  "Créancier", "Débiteur", "Fournisseur", "Client",
  "Bailleur", "Locataire", "Maître d'ouvrage", "Prestataire",
];

const FAQ = [
  { q: "CONFILEX donne-t-il des conseils juridiques ?",
    a: "Non. CONFILEX est un outil de structuration documentaire. Aucune consultation juridique, aucune analyse des droits des parties, aucune validation de créance n'est effectuée." },
  { q: "La proposition générée est-elle un contrat ?",
    a: "Non. Le module produit uniquement une « proposition de règlement », une synthèse factuelle et un récapitulatif des éléments communiqués. Aucune force contractuelle, aucun engagement juridique — uniquement des confirmations horodatées des conditions déclarées par les parties." },
  { q: "Que devient l'autre partie sans compte CONFILEX ?",
    a: "Elle reçoit un email d'invitation avec un lien sécurisé à token. Elle peut consulter, répondre, contre-proposer et déposer des pièces sans créer de compte. La création de compte est proposée mais non imposée." },
  { q: "Combien de temps mes échanges sont-ils conservés ?",
    a: "Les données sont conservées 18 mois après la dernière activité du dossier (TTL automatique). Vous pouvez exporter la synthèse PDF à tout moment." },
  { q: "Quel format pour le PDF d'export ?",
    a: "Un document PDF intitulé « Synthèse des conditions déclarées par les parties », horodaté, paginé, comportant la clause de conformité en pied de page." },
];

export default function Negociation() {
  const { user } = useAuth();
  const ctaLink = user ? "/negociation/nouveau" : "/login?next=/negociation/nouveau";

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="negociation-landing">
      <Seo
        title="Confilex Négociation — Structurer vos échanges amiables"
        description="CONFILEX Négociation : plateforme de structuration documentaire et de facilitation des échanges amiables. Formalisez une proposition de règlement, sollicitez un délai, proposez un échéancier, déposez vos pièces — dans un environnement sécurisé, horodaté et orienté solution."
        canonical="/negociation"
        keywords={[
          "négociation amiable", "résolution amiable", "proposition de règlement",
          "échéancier de paiement", "structuration documentaire", "Confilex Négociation",
          "délai de paiement", "remise commerciale", "médiation entreprise",
        ]}
        jsonLd={[
          ldService({
            name: "Confilex Négociation — Structuration documentaire des échanges amiables",
            description: "Plateforme de structuration documentaire et de facilitation des échanges amiables entre créanciers, débiteurs, fournisseurs, clients, bailleurs et locataires. CONFILEX ne fournit aucune consultation juridique et ne rédige aucun acte juridique.",
            serviceType: "Document structuring and amicable negotiation facilitation",
            url: "https://confilex.fr/negociation",
          }),
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Confilex Négociation", url: "/negociation" },
          ]),
          ldFAQ(FAQ),
        ]}
      />
      <Navbar variant="light" />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAF7F1] via-white to-[#FAFAF7] pointer-events-none" />
        <div className="absolute -top-32 -right-24 w-[460px] h-[460px] rounded-full bg-[#C9A961]/15 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-3" data-testid="hero-eyebrow">
              CONFILEX · NÉGOCIATION
            </p>
            <h1 className="font-serif text-4xl lg:text-6xl text-[#0B3A2A] leading-[1.05] tracking-tight">
              Structurez vos échanges amiables.
              <span className="block text-[#C9A961] italic">Orientez vos négociations vers la résolution.</span>
            </h1>
            <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Une plateforme de structuration documentaire et de facilitation des échanges amiables.
              CONFILEX vous accompagne pour <b>formaliser une proposition</b>, <b>solliciter un délai</b>,
              <b> proposer un échéancier</b> ou <b>demander une remise commerciale</b> — dans un environnement
              sécurisé, horodaté et orienté solution.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="hero-cta-create">
                <Link to={ctaLink}>
                  Ouvrir un dossier <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-[#C9A961] text-[#0B3A2A]" data-testid="hero-cta-faq">
                <a href="#faq">Comment ça marche ?</a>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-roles">
              {ROLES.map(r => (
                <span key={r} className="text-[11px] px-3 py-1 rounded-full bg-white border border-[#C9A961]/30 text-[#0B3A2A]">
                  {r}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white border border-slate-200 p-7 shadow-xl shadow-[#0B3A2A]/5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#C9A961]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#C9A961] font-medium">Pitch CONFILEX</span>
              </div>
              <p className="font-serif text-xl text-[#0B3A2A] leading-snug">
                « Structurez vos échanges amiables.
                Formalisez vos propositions de règlement.
                Conservez une trace horodatée. »
              </p>
              <div className="mt-5 pt-5 border-t border-slate-200 space-y-2 text-sm text-slate-700">
                <div className="flex items-start gap-2"><Clock className="w-4 h-4 text-[#C9A961] mt-0.5" /> Horodatage à la seconde</div>
                <div className="flex items-start gap-2"><FileText className="w-4 h-4 text-[#C9A961] mt-0.5" /> PDF prêt à archiver</div>
                <div className="flex items-start gap-2"><Lock className="w-4 h-4 text-[#C9A961] mt-0.5" /> Accès par lien sécurisé</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILIERS */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="pillars">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-3">Ce que vous obtenez</p>
          <h2 className="font-serif text-3xl text-[#0B3A2A] mb-3">Un environnement complet pour structurer vos négociations.</h2>
          <p className="text-slate-600">Tout ce qu'il faut pour conserver une trace exploitable, sans entrer dans l'arène contentieuse.</p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-xl bg-white border border-slate-200 p-6 hover:border-[#C9A961] transition">
              <div className="w-11 h-11 rounded-lg bg-[#FAF7F1] flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#C9A961]" />
              </div>
              <p className="font-serif text-lg text-[#0B3A2A] mb-1">{title}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PARCOURS */}
      <section className="bg-[#0B3A2A] text-white py-16" data-testid="steps">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-3">Parcours utilisateur</p>
          <h2 className="font-serif text-3xl mb-10">5 étapes pour structurer un dossier de négociation.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map(s => (
              <div key={s.n} className="rounded-xl bg-white/5 border border-white/10 p-5 backdrop-blur-sm">
                <div className="w-9 h-9 rounded-full bg-[#C9A961] text-[#0B3A2A] flex items-center justify-center font-bold mb-3">
                  {s.n}
                </div>
                <p className="font-serif text-base text-white mb-2">{s.label}</p>
                <p className="text-xs text-white/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button asChild size="lg" className="bg-[#C9A961] hover:bg-[#D4B478] text-[#0B3A2A]" data-testid="steps-cta">
              <Link to={ctaLink}>Ouvrir mon premier dossier <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* DÉCOUVERTE pour utilisateurs sans compte */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center" data-testid="discovery">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-3">Vous découvrez CONFILEX ?</p>
        <h2 className="font-serif text-3xl text-[#0B3A2A] mb-5">
          Une plateforme pensée pour vos négociations <span className="italic">amiables</span>.
        </h2>
        <p className="text-slate-600 leading-relaxed max-w-2xl mx-auto mb-4">
          CONFILEX est une plateforme conçue pour les entreprises, professionnels et particuliers
          souhaitant structurer leurs échanges, centraliser leurs documents et favoriser la recherche
          de solutions amiables.
        </p>
        <p className="text-slate-600 italic mb-7">
          Si vous n'êtes pas encore utilisateur de CONFILEX, nous serions ravis de vous accueillir
          à cette occasion.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button asChild variant="outline" data-testid="discovery-register">
            <Link to="/register">Créer un compte</Link>
          </Button>
          <Button asChild className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="discovery-create">
            <Link to={ctaLink}>Démarrer un dossier</Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white border-y border-slate-200 py-16" data-testid="faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-3">Questions fréquentes</p>
          <h2 className="font-serif text-3xl text-[#0B3A2A] mb-8">Ce que vous devez savoir avant de commencer.</h2>
          <div className="space-y-4">
            {FAQ.map((f, idx) => (
              <details key={idx} className="group rounded-lg border border-slate-200 bg-[#FAFAF7] open:bg-white open:border-[#C9A961]/40">
                <summary className="cursor-pointer px-5 py-4 font-medium text-[#0B3A2A] flex items-center justify-between">
                  <span>{f.q}</span>
                  <span className="text-[#C9A961] group-open:rotate-180 transition">▾</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CLAUSE DE CONFORMITÉ — visible sur la landing publique */}
      <section className="bg-[#FAF7F1] border-t border-[#C9A961]/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-8 flex items-start gap-3 text-xs text-[#0B3A2A] leading-relaxed" data-testid="compliance-notice">
          <ShieldCheck className="w-5 h-5 text-[#C9A961] mt-0.5 shrink-0" />
          <p><b>Mention de conformité :</b> {COMPLIANCE}</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
